<template>
  <div>
    <h1 class="title">{{ title }}</h1>

    <app-cells>
      <app-filters
        v-model="statusFilter"
        :filters="statusFilters"
        :name="`${kind}-status`"
        @change="onFilterChange"
      />
      <app-filters
        v-if="isMisconduct"
        v-model="categoryFilter"
        :filters="categoryFilters"
        name="misconduct-category"
        @change="onFilterChange"
      />
    </app-cells>

    <app-spinner v-if="!isLoaded" />
    <template v-else>
      <v-client-table
        :class="{ 'table-default--loading': tableLoading }"
        :columns="tableColumns"
        :data="items"
        :options="tableOptions"
        class="table-default feedback-table"
      >
        <template #h__category><div class="table-default__left">Category</div></template>
        <template #h__text><div class="table-default__left">Text</div></template>
        <template #h__employee><div class="table-default__left">Employee</div></template>

        <template #category="{ row }">
          <div class="table-default__left">{{ categoryName(row.category) }}</div>
        </template>
        <template #text="{ row }">
          <div class="table-default__left feedback-table__text">{{ row.text || '-' }}</div>
        </template>
        <template #answer="{ row }">
          <div class="table-default__left feedback-table__text">{{ row.answer || '-' }}</div>
        </template>
        <template #employee="{ row }">
          <div class="table-default__left">{{ employeeName(row.employee) }}</div>
        </template>
        <template #status="{ row }">
          <span
            :style="{ backgroundColor: statusColor(row.status && row.status.code) }"
            class="status-tag"
          >
            {{ statusName(row.status) }}
          </span>
        </template>
        <template #created_at="{ row }">
          <span class="table-default__nowrap">{{ formatDateTime(row.created_at) }}</span>
        </template>
        <template #updated_at="{ row }">
          <span class="table-default__nowrap">{{ formatDateTime(row.updated_at) }}</span>
        </template>
        <template #actions="{ row }">
          <div class="feedback-table__status-actions">
            <button v-if="isMisconduct" class="feedback-table__answer-button" type="button" :disabled="actionLoadingId !== null" @click="openAnswer(row)">
              {{ row.answer ? 'Edit answer' : 'Answer' }}
            </button>
            <button
              v-for="status in availableStatuses(row)"
              :key="status.code"
              :disabled="actionLoadingId !== null"
              :style="{ backgroundColor: status.color }"
              class="feedback-table__status-button"
              type="button"
              @click="openStatusConfirmation(row, status)"
            >
              {{ status.name }}
            </button>
          </div>
        </template>
      </v-client-table>

      <app-cells position="between">
        <paginate
          v-if="pagination.pages > 1"
          v-model="pagination.page"
          :click-handler="onClickPagination"
          :container-class="'pagination'"
          :next-class="'pagination__item pagination__item--next'"
          :next-link-class="'pagination__link pagination__link--next'"
          :next-text="'>'"
          :page-class="'pagination__item'"
          :page-count="pagination.pages"
          :page-link-class="'pagination__link'"
          :prev-class="'pagination__item pagination__item--prev'"
          :prev-link-class="'pagination__link pagination__link--prev'"
          :prev-text="'<'"
        />
        <div v-else></div>
        <app-table-records
          :all="pagination.count"
          :count="items.length"
          :page="pagination.page"
          :limit="pageSize"
        />
      </app-cells>
    </template>

    <modal
      name="feedback-status-confirmation"
      :width="400"
      :height="'auto'"
      :adaptive="true"
      :click-to-close="false"
      draggable=".popup__title"
      class="modal-not-overflow"
    >
      <div class="popup">
        <button class="popup__close" type="button" @click="closeStatusConfirmation"></button>
        <div class="popup__title">Change feedback status</div>
        <div class="popup__body">
          <div class="popup__subt">Are you sure you want to change the status?</div>
          <app-cells :indent="false" position="center">
            <app-button
              theme="dark"
              :disabled="actionLoadingId !== null"
              @click.native="closeStatusConfirmation"
            >
              Cancel
            </app-button>
            <app-button
              ref="statusSubmit"
              :disabled="actionLoadingId !== null"
              @click.native="confirmStatusChange"
            >
              Change status
            </app-button>
          </app-cells>
        </div>
      </div>
    </modal>
    <modal name="misconduct-answer" :width="520" :height="'auto'" :adaptive="true" :click-to-close="false" class="modal-not-overflow">
      <div class="popup">
        <button class="popup__close" type="button" @click="closeAnswer"></button>
        <div class="popup__title">Management answer</div>
        <div class="popup__body">
          <app-form-group label="Answer" label-for="misconduct-answer-text" required>
            <app-textarea id="misconduct-answer-text" v-model="answerText" placeholder="Write a response for the employee" />
          </app-form-group>
          <app-cells :indent="false" position="center">
            <app-button theme="dark" :disabled="actionLoadingId !== null" @click.native="closeAnswer">Cancel</app-button>
            <app-button v-if="answerText.trim()" ref="answerSubmit" :disabled="actionLoadingId !== null" @click.native="saveAnswer">Save answer</app-button>
          </app-cells>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import { format, parseISO } from 'date-fns'
import { getEmployeesFeedback, patchEmployeesFeedback } from '@/http'
import { FEEDBACK_STATUSES, getFeedbackStatusColor, getFeedbackStatusName } from '@/utils/feedback-status'

const MISCONDUCT_CATEGORIES = [
  { code: 'colleagues', name: 'Colleagues' },
  { code: 'management', name: 'Management' },
  { code: 'working_conditions', name: 'Working conditions' },
  { code: 'other', name: 'Other' },
]

export default {
  name: 'EmployeesFeedbackTable',
  props: {
    kind: {
      type: String,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      items: [],
      statusFilter: '',
      categoryFilter: '',
      categoryOptions: MISCONDUCT_CATEGORIES,
      pagination: {
        pages: 1,
        page: 1,
        count: 0,
      },
      pageSize: 50,
      isLoaded: false,
      tableLoading: false,
      actionLoadingId: null,
      selectedFeedback: null,
      selectedStatus: null,
      answeringFeedback: null,
      answerText: '',
      statusOptions: FEEDBACK_STATUSES,
    }
  },
  computed: {
    isMisconduct() {
      return this.kind === 'misconduct'
    },
    statusFilters() {
      return [
        { value: '', label: 'All statuses' },
        ...this.statusOptions.map(status => ({ value: status.code, label: status.name })),
      ]
    },
    categoryFilters() {
      return [
        { value: '', label: 'All categories' },
        ...this.categoryOptions.map(category => ({ value: String(category.code), label: category.name })),
      ]
    },
    hasEmployeeData() {
      return this.isMisconduct && this.items.some(item => Object.prototype.hasOwnProperty.call(item, 'employee'))
    },
    tableColumns() {
      const columns = this.isMisconduct ? ['category', 'text', 'answer'] : ['text']
      if (this.hasEmployeeData) columns.push('employee')
      return [...columns, 'status', 'created_at', 'updated_at', 'actions']
    },
    tableOptions() {
      return {
        headings: {
          category: 'Category',
          text: this.isMisconduct ? 'Appeal' : 'Suggestion',
          answer: 'Management answer',
          employee: 'Employee',
          status: 'Status',
          created_at: 'Created',
          updated_at: 'Last changed',
          actions: 'Change status',
        },
        pagination: { show: false },
        sortable: [],
        perPage: this.pageSize,
        texts: { noResults: `No ${this.isMisconduct ? 'misconduct reports' : 'employee suggestions'}` },
        rowClassCallback: () => 'table-default__row',
        resizableColumns: false,
      }
    },
  },
  created() {
    this.fetchItems()
  },
  methods: {
    fetchItems() {
      this.tableLoading = this.isLoaded
      return getEmployeesFeedback({
        kind: this.kind,
        status: this.statusFilter,
        category: this.isMisconduct ? this.categoryFilter : null,
        page: this.pagination.page,
        page_size: this.pageSize,
      })
        .then(response => {
          const data = response.data
          this.items = Array.isArray(data) ? data : data.results || []
          this.pagination.pages = Array.isArray(data) ? 1 : data.pages || 1
          this.pagination.count = Array.isArray(data) ? data.length : data.count || 0
        })
        .catch(() => {
          this.items = []
          this.pagination.pages = 1
          this.pagination.count = 0
        })
        .finally(() => {
          this.isLoaded = true
          this.tableLoading = false
        })
    },
    onFilterChange() {
      this.pagination.page = 1
      this.fetchItems()
    },
    onClickPagination(page) {
      this.pagination.page = page
      this.fetchItems()
    },
    availableStatuses(row) {
      return this.statusOptions.filter(status => status.code !== (row.status && row.status.code))
    },
    openStatusConfirmation(row, status) {
      if (this.actionLoadingId !== null) return
      this.selectedFeedback = row
      this.selectedStatus = status
      this.$modal.show('feedback-status-confirmation')
    },
    closeStatusConfirmation() {
      if (this.actionLoadingId !== null) return
      this.$modal.hide('feedback-status-confirmation')
      this.selectedFeedback = null
      this.selectedStatus = null
    },
    confirmStatusChange() {
      if (!this.selectedFeedback || !this.selectedStatus || this.actionLoadingId !== null) return

      this.actionLoadingId = this.selectedFeedback.id
      this.$refs.statusSubmit.preload = true
      patchEmployeesFeedback(this.selectedFeedback.id, { status: this.selectedStatus.code })
        .then(() => this.fetchItems())
        .then(() => {
          this.$modal.hide('feedback-status-confirmation')
          this.selectedFeedback = null
          this.selectedStatus = null
          this.$notify({ type: 'success', title: 'Feedback status changed' })
        })
        .catch(() => {})
        .finally(() => {
          this.actionLoadingId = null
          this.$refs.statusSubmit.preload = false
        })
    },
    openAnswer(row) {
      if (this.actionLoadingId !== null) return
      this.answeringFeedback = row
      this.answerText = row.answer || ''
      this.$modal.show('misconduct-answer')
    },
    closeAnswer() {
      if (this.actionLoadingId !== null) return
      this.$modal.hide('misconduct-answer')
      this.answeringFeedback = null
      this.answerText = ''
    },
    saveAnswer() {
      const answer = this.answerText.trim()
      if (!this.answeringFeedback || !answer || this.actionLoadingId !== null) return
      this.actionLoadingId = this.answeringFeedback.id
      this.$refs.answerSubmit.preload = true
      patchEmployeesFeedback(this.answeringFeedback.id, { answer })
        .then(() => this.fetchItems())
        .then(() => { this.$modal.hide('misconduct-answer'); this.answeringFeedback = null; this.answerText = ''; this.$notify({ type: 'success', title: 'Management answer saved' }) })
        .finally(() => { this.actionLoadingId = null; if (this.$refs.answerSubmit) this.$refs.answerSubmit.preload = false })
    },
    normalizeCategory(category) {
      if (!category) return null
      const code = typeof category === 'string' ? category : category.code || category.id
      if (code === undefined || code === null) return null
      const knownCategory = MISCONDUCT_CATEGORIES.find(item => item.code === String(code))
      const responseName = typeof category === 'object' && (category.name || category.label)
      return { code, name: knownCategory ? knownCategory.name : responseName || String(code) }
    },
    categoryName(category) {
      const normalizedCategory = this.normalizeCategory(category)
      return normalizedCategory ? normalizedCategory.name : '-'
    },
    employeeName(employee) {
      return employee && employee.full_name ? employee.full_name : '-'
    },
    statusColor(code) {
      return getFeedbackStatusColor(code)
    },
    statusName(status) {
      return getFeedbackStatusName(status)
    },
    formatDateTime(date) {
      return date ? format(parseISO(date), 'dd MMM yyyy HH:mm') : '-'
    },
  },
}
</script>

<style lang="sass" scoped>
.feedback-table__text
  min-width: 260px
  max-width: 600px
  white-space: pre-wrap
  overflow-wrap: anywhere

.feedback-table__status-actions
  display: flex
  align-items: center
  justify-content: center
  gap: 6px

.feedback-table__status-button
  padding: 6px 14px
  border: 0
  border-radius: 20px
  color: #fff
  font-family: inherit
  font-size: 13px
  font-weight: 600
  line-height: 1.2

.feedback-table__answer-button
  padding: 6px 14px
  border-radius: 20px
  background: #77b017
  color: #fff
  font-size: 13px
  font-weight: 600
  white-space: nowrap
  cursor: pointer

  &:hover
    opacity: .85

  &:disabled
    opacity: .5
    cursor: default
</style>
